import { fg } from '@atlaskit/platform-feature-flags';

import type { Edition, Product } from '../types';

import { getChargeElementKey } from './get-charge-element-key';
import { getJiraApplicationKey } from './get-jira-application-key';
import { getProductCollection } from './get-product-collection';
import { getHamsKey } from './product-to-hams-key';
import { dialogIdToKey } from './use-mark-dialog-as-seen';

/**
 * Returns the variables required for the root Edition Awareness query.
 */
export function getQueryConfig(
	product: Product,
	edition: Edition,
	cloudId: string,
	accountId?: string | null,
) {
	const isStandardTrialsUserAndStorageLimitBreakersEnabledJira =
		product === 'jira' &&
		edition === 'standard' &&
		fg('jira_edition_awareness_standard_trials_gate');
	const isStandardTrialsUserAndStorageLimitBreakersEnabledConnie =
		product === 'confluence' &&
		edition === 'standard' &&
		fg('connie_edition_awareness_standard_trials_gate');
	const isStorageHardLimitEnabled =
		product === 'jira' && edition === 'free' && !!fg('jira-storage-hard-limit-gate'); // Workaround to ensure fg is not undefined to avoid errors in unit tests - https://hello.atlassian.net/wiki/x/Uy4HSgE
	const isStandardTrialsUserAndStorageLimitBreakersEnabled =
		isStandardTrialsUserAndStorageLimitBreakersEnabledJira ||
		isStandardTrialsUserAndStorageLimitBreakersEnabledConnie;
	const isCollectionEabEnabled = getProductCollection(product)?.isEnabled ?? false;
	const isBillingAdminEnabled =
		(product === 'jira' || product === 'confluence') &&
		fg('should_check_billing_admin_ea_query_config_gate');
	const shouldCheckForPaymentDetails =
		edition === 'free' &&
		(product === 'jira-service-management' || product === 'jira-product-discovery') &&
		fg('jsm_jpd_free_to_standard_ea_update_gate');

	const shouldIncludeProfile = product === 'confluence' && fg('ea_gups_confluence');
	const shouldCheckHasSeenModal = product === 'confluence' && fg('conf_user_preferences_dialog');

	return {
		shouldSkip:
			product === 'jira-product-discovery' && fg('jpd_disable_edition_awareness') === true,
		shouldCheckForChangeOffering: edition === 'free',
		shouldCheckForDirectBuyChangeOffering: isStorageHardLimitEnabled,
		shouldCheckForTrial: edition === 'premium' || edition === 'standard',
		cloudId,
		productKey: getHamsKey(product),
		shouldCheckForChargeQuantities:
			edition === 'free' || isStandardTrialsUserAndStorageLimitBreakersEnabled,
		// The entitlement filter ensures that we apply the `inTrialOrPreDunning` only when an entitlement is
		// on a non free edition. The filter is based on the trial end timestamp inside of TCS, this means that
		// if a customer downgrades their product to free while on a trial, then they will only show up
		// if inTrialOrPreDunning is set to true as the filter assumes they are still on trial.
		entitlementFilter: edition !== 'free' ? { inTrialOrPreDunning: true } : null,
		chargeElement: getChargeElementKey(product),
		shouldCheckBillingAdmin: isBillingAdminEnabled,
		shouldCheckForCollection: isCollectionEabEnabled,
		shouldCheckForStorageUsageJira:
			isStandardTrialsUserAndStorageLimitBreakersEnabledJira || isStorageHardLimitEnabled,
		applicationKey: getJiraApplicationKey(product) ?? 'JIRA_SOFTWARE',
		shouldCheckForStorageUsageConfluence: isStandardTrialsUserAndStorageLimitBreakersEnabledConnie,
		// AccountID is a required value, but the condition will ensure it is not used if undefined/null
		accountId: accountId ?? '',
		shouldCheckForUserEmail: !!accountId && product === 'jira' && edition === 'standard',
		shouldCheckForStorageLimit: isStorageHardLimitEnabled,
		shouldCheckForPaymentDetails,
		shouldIncludeProfile,
		shouldCheckHasSeenModal,
		onboardingStateKeys: [dialogIdToKey('feature-onboarding')],
	};
}
